import {
    QuoteService,
    OptionPricingItem,
    toBig, DevicePricingItem
} from 'quote-domain-core';
import { Big } from 'big.js';
import { VtArticleCorridorCalculator } from './vt-article-corridor-calculator';
import {PricingItemCorridorContext} from "./pricing-item-corridor-context";
import {singleton} from "tsyringe";


@singleton()
export class VtCorridorCalculationService {
    constructor(private readonly articleCorridorCalculator: VtArticleCorridorCalculator) {
    }

    public calculate(quoteService: QuoteService, referential: any, referentialService: any, region: string): void {
        const logisticPricePerService = this.calculateLogisticPrice(quoteService, referential, referentialService);
        const totalStockForAllArticles = this.calculateTotalStockForAllArticles(quoteService);

        const corridorContext: PricingItemCorridorContext = {
            referentialService,
            region,
            contractInfoInputs: quoteService.contractInfoInputs,
            logisticPricePerService,
            totalStockForAllArticles
        };

        (quoteService.customGroups ?? []).forEach(customGroup => {
            (customGroup.pricingItems ?? []).forEach(pricingItem => {
                if (pricingItem.isCmc) {
                    return;
                }
                if (pricingItem instanceof OptionPricingItem) {
                    this.articleCorridorCalculator.applyPccPricing(pricingItem.option);
                } else if (pricingItem instanceof DevicePricingItem && pricingItem.device) {
                    this.articleCorridorCalculator.calculate(
                        pricingItem.device,
                        corridorContext,
                        pricingItem.userInputs
                    );
                }
            });
        });
    }

    public calculateLogisticPrice(quoteService: QuoteService, referential: any, referentialService: any): Big {
        const logisticsMargin = toBig(referentialService?.logisticsMargin?.logisticsMargin);
        const timeOnSiteInHour = toBig(referentialService?.timeOnSite?.timeOnSiteInMin).div(60);
        const salaryCost = toBig(this.findCostValue(referentialService?.cost, 'logistic-salaryCost'));
        const siteNbr = toBig(quoteService.contractInfoInputs?.siteNbr);
        const freqVisitPerWeek = this.getFreqVisitPerWeek(quoteService, referential);
        const excludingSalaryCost = toBig(this.findCostValue(referentialService?.cost, 'logistic-excludingSalaryCost'));
        const result1 = timeOnSiteInHour.times(salaryCost).plus(siteNbr.times(freqVisitPerWeek).times(excludingSalaryCost));
        const result3 = new Big(1).div(new Big(1).minus(logisticsMargin));
        return result1.times(result3);
    }

    private calculateTotalStockForAllArticles(quoteService: QuoteService): Big {
        return (quoteService.customGroups ?? []).reduce((groupTotal, customGroup) => {
            const itemTotal = (customGroup.pricingItems ?? []).reduce((sum, pricingItem) => {
                if (!(pricingItem instanceof DevicePricingItem)) {
                    return sum;
                }
                const quantity = toBig(pricingItem.userInputs?.quantity);
                const stockVt = toBig(pricingItem.userInputs?.stockVT);
                return sum.plus(quantity.times(stockVt));
            }, new Big(0));

            return groupTotal.plus(itemTotal);
        }, new Big(0));
    }

    private getFreqVisitPerWeek(quoteService: QuoteService, referential: any): Big {
        const visitingFrequencyInput = quoteService.contractInfoInputs?.changeOverFrequency;
        const visitingFrequencyInputValue = (referential?.frequencies ?? []).find(
            (freq: { frequency: any }) => freq.frequency === visitingFrequencyInput
        );

        if (!visitingFrequencyInputValue) {
            return new Big('0.009659614');
        }

        const deliveryWeeks = toBig(visitingFrequencyInputValue?.deliveryWeeks);
        if (deliveryWeeks.eq(0)) {
            return new Big(0);
        }
        return new Big(1).div(deliveryWeeks);
    }

    private findCostValue(cost: any, name: string): any {
        return cost?.find((item: any) => item.name === name)?.value;
    }
}

